import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  BookOpenIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  XIcon,
  LayersIcon,
  HashIcon,
  ClockIcon,
  CalculatorIcon,
  AwardIcon,
  ChevronDownIcon,
  CheckIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import learningLevelsData from '../../data/learningLevels.json';

const DailyExercises = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loadingExercise, setLoadingExercise] = useState(false);
  
  // Organizational data
  const [countries, setCountries] = useState([]);
  const [learningLevels, setLearningLevels] = useState([]);
  const [loadingOrgData, setLoadingOrgData] = useState(true);
  const [levelsDropdownOpen, setLevelsDropdownOpen] = useState(false);
  const levelsDropdownRef = useRef(null);

  // Close levels dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (levelsDropdownRef.current && !levelsDropdownRef.current.contains(event.target)) {
        setLevelsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Exercise form state
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    description: '',
    totalRounds: 0,
    status: 'active',
    countryId: '',
    levelIds: [],
    maxAttemptsPerDay: 5,
    rounds: []
  });
  
  // New round state
  const [newRound, setNewRound] = useState({
    roundId: 1,
    operationType: 'addition',
    totalNumbers: 3,
    digitsPerNumber: 2,
    displayTimeMs: 2000,
    difficulty: 'easy',
    score: 5,
    levelId: ''
  });

  // Fetch exercises
  const fetchExercises = async () => {
    setLoading(true);
    try {
      const response = await api.get('/exercises?page=1&limit=100');
      if (response.data.success) {
        setExercises(response.data.data.exercises || []);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error(error.response?.data?.message || t('dailyExercises.fetchError'), {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch organizational data
  const fetchOrganizationalData = async () => {
    setLoadingOrgData(true);
    try {
      // Fetch countries
      const countriesResponse = await api.get('/organization/countries?page=1&limit=100');
      if (countriesResponse.data.success) {
        setCountries(countriesResponse.data.data.countries || []);
      }
      
      // Fetch learning levels
      const levelsResponse = await api.get('/organization/learning-levels?page=1&limit=100');
      if (levelsResponse.data.success) {
        setLearningLevels(levelsResponse.data.data.learningLevels || []);
      }
    } catch (error) {
      console.error('Error fetching organizational data:', error);
      toast.error(t('dailyExercises.orgDataFetchError'), {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setLoadingOrgData(false);
    }
  };

  useEffect(() => {
    fetchOrganizationalData();
    fetchExercises();
  }, []);

  // Open add modal
  const openAddModal = () => {
    setShowAddModal(true);
    setExerciseForm({
      name: '',
      description: '',
      totalRounds: 0,
      status: 'active',
      countryId: '',
      levelIds: [],
      maxAttemptsPerDay: 5,
      rounds: []
    });
    setNewRound({
      roundId: 1,
      operationType: 'addition',
      totalNumbers: 3,
      digitsPerNumber: 2,
      displayTimeMs: 2000,
      difficulty: 'easy',
      score: 5,
      levelId: learningLevels.length > 0 ? learningLevels[0].id.toString() : ''
    });
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setEditingExercise(null);
    setExerciseForm({
      name: '',
      description: '',
      totalRounds: 0,
      status: 'active',
      countryId: '',
      levelIds: [],
      maxAttemptsPerDay: 5,
      rounds: []
    });
    setNewRound({
      roundId: 1,
      operationType: 'addition',
      totalNumbers: 3,
      digitsPerNumber: 2,
      displayTimeMs: 2000,
      difficulty: 'easy',
      score: 5,
      levelId: learningLevels.length > 0 ? learningLevels[0].id.toString() : ''
    });
  };

  // Open edit modal
  const openEditModal = async (exercise) => {
    setEditingExercise(exercise);
    setLoadingExercise(true);
    setShowAddModal(true);
    
    try {
      const response = await api.get(`/exercises/${exercise.exerciseId || exercise.id}`);
      
      if (response.data.success) {
        const exerciseData = response.data.data;
        const levelIdsVal = exerciseData.levelIds?.length
          ? exerciseData.levelIds.map(id => parseInt(id)).filter(Boolean)
          : (exerciseData.levelId ? [parseInt(exerciseData.levelId)] : []);
        setExerciseForm({
          name: exerciseData.name || '',
          description: exerciseData.description || '',
          totalRounds: exerciseData.totalRounds || 0,
          status: exerciseData.status || 'active',
          countryId: exerciseData.countryId?.toString() || '',
          levelIds: levelIdsVal,
          maxAttemptsPerDay: exerciseData.maxAttemptsPerDay || 5,
          rounds: exerciseData.rounds || []
        });
        
        // Set next round ID
        const nextId = exerciseData.rounds && exerciseData.rounds.length > 0
          ? Math.max(...exerciseData.rounds.map(r => r.roundId)) + 1
          : 1;
        setNewRound({
          roundId: nextId,
          operationType: 'addition',
          totalNumbers: 3,
          digitsPerNumber: 2,
          displayTimeMs: 2000,
          difficulty: 'easy',
          score: 5,
          levelId: learningLevels.length > 0 ? learningLevels[0].id.toString() : ''
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch exercise');
      }
    } catch (error) {
      console.error('Error fetching exercise details:', error);
      toast.error(error.response?.data?.message || t('dailyExercises.fetchError'), {
        duration: 3000,
        position: 'top-center'
      });
      closeAddModal();
    } finally {
      setLoadingExercise(false);
    }
  };

  // Add round locally
  const addRoundLocally = () => {
    if (!newRound.totalNumbers || !newRound.digitsPerNumber || !newRound.displayTimeMs || newRound.score === '' || !newRound.levelId) {
      toast.error(t('dailyExercises.roundDataRequired'), {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }

    const nextId = exerciseForm.rounds.length > 0 
      ? Math.max(...exerciseForm.rounds.map(r => r.roundId)) + 1 
      : 1;

    const roundToAdd = {
      ...newRound,
      roundId: nextId
    };

    setExerciseForm(prev => ({
      ...prev,
      rounds: [...prev.rounds, roundToAdd],
      totalRounds: prev.rounds.length + 1
    }));

    setNewRound({
      roundId: nextId + 1,
      operationType: 'addition',
      totalNumbers: 3,
      digitsPerNumber: 2,
      displayTimeMs: 2000,
      difficulty: 'easy',
      score: 5,
      levelId: learningLevels.length > 0 ? learningLevels[0].id.toString() : ''
    });
  };

  // Delete round locally
  const deleteRoundLocally = (roundId) => {
    setExerciseForm(prev => ({
      ...prev,
      rounds: prev.rounds.filter(r => r.roundId !== roundId),
      totalRounds: prev.rounds.length - 1
    }));
  };

  // Handle form input change
  const handleFormChange = (field, value) => {
    setExerciseForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle level in levelIds (multi-select)
  const toggleLevelId = (levelId) => {
    const id = typeof levelId === 'number' ? levelId : parseInt(levelId);
    setExerciseForm(prev => {
      const current = prev.levelIds || [];
      const hasId = current.includes(id);
      const next = hasId ? current.filter(l => l !== id) : [...current, id].sort((a, b) => a - b);
      return { ...prev, levelIds: next };
    });
  };

  // Handle round input change
  const handleRoundChange = (field, value) => {
    setNewRound(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Create exercise
  const createExercise = async () => {
    // Validation
    if (!exerciseForm.name || !exerciseForm.description || !exerciseForm.countryId) {
      toast.error(t('dailyExercises.requiredFields'), {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }

    const levelIds = [...(exerciseForm.levelIds || [])].map(id => parseInt(id)).filter(Boolean).sort((a, b) => a - b);

    if (levelIds.length === 0) {
      toast.error(t('dailyExercises.requiredFields'), {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }

    if (exerciseForm.rounds.length === 0) {
      toast.error(t('dailyExercises.atLeastOneRound'), {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }

    const roundsWithLevelId = exerciseForm.rounds.filter(r => r.levelId);
    if (roundsWithLevelId.length !== exerciseForm.rounds.length) {
      toast.error(t('dailyExercises.roundDataRequired'), {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }

    const roundsPayload = exerciseForm.rounds.map(r => ({
      roundId: r.roundId,
      operationType: r.operationType,
      totalNumbers: r.totalNumbers,
      digitsPerNumber: r.digitsPerNumber,
      displayTimeMs: r.displayTimeMs,
      difficulty: r.difficulty,
      score: r.score,
      levelId: parseInt(r.levelId)
    }));

    setIsCreating(true);
    try {
      const payload = {
        name: exerciseForm.name,
        description: exerciseForm.description,
        totalRounds: exerciseForm.rounds.length,
        status: exerciseForm.status,
        countryId: parseInt(exerciseForm.countryId),
        levelIds,
        maxAttemptsPerDay: parseInt(exerciseForm.maxAttemptsPerDay),
        rounds: roundsPayload
      };

      const response = await api.post('/exercises/create', payload);

      if (response.data.success) {
        toast.success(response.data.message || t('dailyExercises.createSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        closeAddModal();
        fetchExercises();
      } else {
        throw new Error(response.data.message || 'Failed to create exercise');
      }
    } catch (error) {
      console.error('Error creating exercise:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          t('dailyExercises.createError');
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Update exercise
  const updateExercise = async () => {
    if (!editingExercise) return;

    // Validation
    if (!exerciseForm.name || !exerciseForm.description) {
      toast.error(t('dailyExercises.requiredFields'), {
        duration: 3000,
        position: 'top-center'
      });
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        name: exerciseForm.name,
        description: exerciseForm.description,
        status: exerciseForm.status
      };

      const exerciseId = editingExercise.exerciseId || editingExercise.id;
      const response = await api.put(`/exercises/${exerciseId}`, payload);

      if (response.data.success) {
        toast.success(response.data.message || t('dailyExercises.updateSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        closeAddModal();
        fetchExercises();
      } else {
        throw new Error(response.data.message || 'Failed to update exercise');
      }
    } catch (error) {
      console.error('Error updating exercise:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          t('dailyExercises.updateError');
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle save (create or update)
  const handleSave = () => {
    if (editingExercise) {
      updateExercise();
    } else {
      createExercise();
    }
  };

  // Delete exercise
  const deleteExercise = async (exercise) => {
    const exerciseId = exercise.exerciseId || exercise.id;
    const exerciseName = exercise.name || '';

    // Confirm deletion
    if (!window.confirm(t('dailyExercises.confirmDelete', { name: exerciseName }))) {
      return;
    }

    try {
      const response = await api.delete(`/exercises/${exerciseId}`);

      if (response.data.success) {
        toast.success(response.data.message || t('dailyExercises.deleteSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        fetchExercises();
      } else {
        throw new Error(response.data.message || 'Failed to delete exercise');
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          t('dailyExercises.deleteError');
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center'
      });
    }
  };

  // Get operation type translation
  const getOperationTypeLabel = (type) => {
    const types = {
      addition: t('competitions.addition'),
      subtraction: t('competitions.subtraction'),
      multiplication: t('competitions.multiplication'),
      division: t('competitions.division'),
      mixed: t('competitions.mixed')
    };
    return types[type] || type;
  };

  // Get difficulty label
  const getDifficultyLabel = (difficulty) => {
    const difficulties = {
      easy: t('dailyExercises.difficultyOptions.easy'),
      medium: t('dailyExercises.difficultyOptions.medium'),
      hard: t('dailyExercises.difficultyOptions.hard')
    };
    return difficulties[difficulty] || difficulty;
  };

  // Get country name by ID (with translation)
  const getCountryName = (countryId) => {
    if (!countryId) return '-';
    const country = countries.find(c => c.id === countryId);
    return country ? getCountryTranslation(country.name, currentLanguage) : countryId;
  };

  // Get level name by ID (with translation)
  const getLevelName = (levelId) => {
    if (!levelId) return '-';
    const level = learningLevels.find(l => l.id === levelId || l.id === parseInt(levelId));
    if (!level) return levelId;
    const translations = learningLevelsData[level.name] || {};
    return translations[currentLanguage] || level.name;
  };

  // Get level names display for levelIds array or levelId (backward compat)
  const getLevelNamesDisplay = (levelIdsOrId) => {
    if (!levelIdsOrId) return '-';
    const ids = Array.isArray(levelIdsOrId) ? levelIdsOrId : [levelIdsOrId];
    if (ids.length === 0) return '-';
    return ids.map(id => getLevelName(id)).filter(Boolean).join(', ') || '-';
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dailyExercises.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('dailyExercises.description')}
          </p>
        </div>
        <button
          onClick={openAddModal}
          className={`flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          <PlusIcon className="w-5 h-5" />
          {t('dailyExercises.addExercise')}
        </button>
      </div>

      {/* Exercises List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <LoadingSpinner />
          </div>
        ) : exercises.length === 0 ? (
          <div className="p-12 text-center">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('dailyExercises.noExercises')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('dailyExercises.noExercisesDesc')}</p>
            <button
              onClick={openAddModal}
              className={`mt-6 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <PlusIcon className="w-5 h-5" />
              {t('dailyExercises.addFirstExercise')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('dailyExercises.exerciseName')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('dailyExercises.level')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('dailyExercises.country')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('dailyExercises.totalRounds')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('dailyExercises.maxAttempts')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('dailyExercises.statusLabel')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exercises.map((exercise) => (
                  <tr key={exercise.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{exercise.name}</div>
                      {exercise.description && (
                        <div className="text-sm text-gray-500">{exercise.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{getLevelNamesDisplay(exercise.levelIds || exercise.levelId)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{getCountryName(exercise.countryId)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{exercise.totalRounds}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{exercise.maxAttemptsPerDay}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        exercise.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {t(`dailyExercises.status.${exercise.status}`)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                          onClick={() => openEditModal(exercise)}
                          className="text-primary-600 hover:text-primary-900"
                          title={t('common.edit')}
                        >
                          <EditIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deleteExercise(exercise)}
                          className="text-red-600 hover:text-red-900"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Exercise Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeAddModal}></div>
            
            <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingExercise ? t('dailyExercises.editExercise') : t('dailyExercises.addExercise')}
                  </h3>
                  <button
                    onClick={closeAddModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* Basic Information */}
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">{t('dailyExercises.basicInfo')}</h4>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('dailyExercises.exerciseName')} *
                        </label>
                        <input
                          type="text"
                          value={exerciseForm.name}
                          onChange={(e) => handleFormChange('name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder={t('dailyExercises.exerciseNamePlaceholder')}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('dailyExercises.description')} *
                        </label>
                        <textarea
                          value={exerciseForm.description}
                          onChange={(e) => handleFormChange('description', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder={t('dailyExercises.descriptionPlaceholder')}
                        />
                      </div>

                      {!editingExercise && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('dailyExercises.country')} *
                            </label>
                            <select
                              value={exerciseForm.countryId}
                              onChange={(e) => handleFormChange('countryId', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              disabled={loadingOrgData}
                            >
                              <option value="">{t('dailyExercises.selectCountry')}</option>
                              {countries.map(country => (
                                <option key={country.id} value={country.id}>
                                  {getCountryTranslation(country.name, currentLanguage)}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="relative" ref={levelsDropdownRef}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('dailyExercises.levels')} *
                            </label>
                            <button
                              type="button"
                              onClick={() => !loadingOrgData && setLevelsDropdownOpen(prev => !prev)}
                              disabled={loadingOrgData}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-between ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                            >
                              <span className="text-sm text-gray-700 truncate">
                                {(exerciseForm.levelIds || []).length === 0
                                  ? t('dailyExercises.selectLevels')
                                  : getLevelNamesDisplay(exerciseForm.levelIds)}
                              </span>
                              <ChevronDownIcon className={`w-4 h-4 text-gray-500 flex-shrink-0 ${levelsDropdownOpen ? 'rotate-180' : ''} transition-transform`} />
                            </button>
                            {levelsDropdownOpen && (
                              <div className={`absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto ${isRTL ? 'right-0' : 'left-0'}`}>
                                {learningLevels.length === 0 ? (
                                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                    {t('dailyExercises.noLevelsAvailable')}
                                  </div>
                                ) : (
                                  learningLevels.map(level => {
                                    const isSelected = (exerciseForm.levelIds || []).includes(level.id);
                                    return (
                                      <button
                                        key={level.id}
                                        type="button"
                                        onClick={() => toggleLevelId(level.id)}
                                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''} ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                                      >
                                        {isSelected && <CheckIcon className="w-4 h-4 text-primary-600 flex-shrink-0" />}
                                        <span className={isSelected ? 'font-medium' : ''}>{getLevelName(level.id)}</span>
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {!editingExercise ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('dailyExercises.maxAttempts')}
                            </label>
                            <input
                              type="number"
                              value={exerciseForm.maxAttemptsPerDay}
                              onChange={(e) => handleFormChange('maxAttemptsPerDay', parseInt(e.target.value) || 5)}
                              min="1"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('dailyExercises.statusLabel')}
                            </label>
                            <select
                              value={exerciseForm.status}
                              onChange={(e) => handleFormChange('status', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="active">{t('dailyExercises.status.active')}</option>
                              <option value="inactive">{t('dailyExercises.status.inactive')}</option>
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('dailyExercises.statusLabel')}
                          </label>
                          <select
                            value={exerciseForm.status}
                            onChange={(e) => handleFormChange('status', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="active">{t('dailyExercises.status.active')}</option>
                            <option value="inactive">{t('dailyExercises.status.inactive')}</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rounds Section - Only show when adding new exercise */}
                  {!editingExercise && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-medium text-gray-900">
                        {t('dailyExercises.rounds')} ({exerciseForm.rounds.length})
                      </h4>
                    </div>

                    {/* Add Round Form */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">{t('dailyExercises.addRound')}</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('dailyExercises.level')} *
                          </label>
                          <select
                            value={newRound.levelId}
                            onChange={(e) => handleRoundChange('levelId', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={loadingOrgData}
                          >
                            <option value="">{t('dailyExercises.selectLevel')}</option>
                            {learningLevels.map(level => (
                              <option key={level.id} value={level.id}>
                                {getLevelName(level.id)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('competitions.operationType')}
                          </label>
                          <select
                            value={newRound.operationType}
                            onChange={(e) => handleRoundChange('operationType', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="addition">{t('competitions.addition')}</option>
                            <option value="subtraction">{t('competitions.subtraction')}</option>
                            <option value="multiplication">{t('competitions.multiplication')}</option>
                            <option value="division">{t('competitions.division')}</option>
                            <option value="mixed">{t('competitions.mixed')}</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('dailyExercises.difficulty')}
                          </label>
                          <select
                            value={newRound.difficulty}
                            onChange={(e) => handleRoundChange('difficulty', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="easy">{t('dailyExercises.difficultyOptions.easy')}</option>
                            <option value="medium">{t('dailyExercises.difficultyOptions.medium')}</option>
                            <option value="hard">{t('dailyExercises.difficultyOptions.hard')}</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('competitions.totalNumbers')}
                          </label>
                          <input
                            type="number"
                            value={newRound.totalNumbers}
                            onChange={(e) => handleRoundChange('totalNumbers', parseInt(e.target.value) || 0)}
                            min="1"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('competitions.digitsPerNumber')}
                          </label>
                          <input
                            type="number"
                            value={newRound.digitsPerNumber}
                            onChange={(e) => handleRoundChange('digitsPerNumber', parseInt(e.target.value) || 0)}
                            min="1"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('competitions.displayTime')} (ms)
                          </label>
                          <input
                            type="number"
                            value={newRound.displayTimeMs}
                            onChange={(e) => handleRoundChange('displayTimeMs', parseInt(e.target.value) || 0)}
                            min="100"
                            step="100"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('competitions.score')}
                          </label>
                          <input
                            type="number"
                            value={newRound.score}
                            onChange={(e) => handleRoundChange('score', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.5"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </div>

                      <button
                        onClick={addRoundLocally}
                        className={`mt-3 flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t('dailyExercises.addRound')}
                      </button>
                    </div>

                    {/* Rounds List */}
                    {exerciseForm.rounds.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <LayersIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm">{t('dailyExercises.noRounds')}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {exerciseForm.rounds.map((round, index) => (
                          <div key={round.roundId} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-700 rounded-full font-semibold text-sm">
                                {round.roundId}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {getOperationTypeLabel(round.operationType)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {round.totalNumbers} {t('competitions.numbers')} × {round.digitsPerNumber} {t('competitions.digits')} | 
                                  {round.displayTimeMs}ms | 
                                  {getDifficultyLabel(round.difficulty)} | 
                                  {t('competitions.score')}: {round.score}
                                  {round.levelId && (
                                    <> | {t('dailyExercises.level')}: {getLevelName(round.levelId)}</>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => deleteRoundLocally(round.roundId)}
                              className="text-red-600 hover:text-red-900"
                              title={t('common.delete')}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>

              <div className={`bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3 ${isRTL ? 'flex-row' : ''}`}>
                {loadingExercise ? (
                  <div className="w-full text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent"></div>
                    <p className="mt-2 text-sm text-gray-500">{t('common.loading')}</p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating || (!editingExercise && exerciseForm.rounds.length === 0)}
                    className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {(isCreating || isUpdating) ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        {t('common.saving')}
                      </>
                    ) : (
                      editingExercise ? t('common.update') : t('common.create')
                    )}
                  </button>
                )}
                {!loadingExercise && (
                  <button
                    type="button"
                    onClick={closeAddModal}
                    disabled={isCreating || isUpdating}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyExercises;
